import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

const SplashPerfect = () => {
  const navigate = useNavigate();
  const [stage, setStage] = useState(0);

  useEffect(() => {
    const timers = [
      setTimeout(() => setStage(1), 500),   // Books/Knowledge
      setTimeout(() => setStage(2), 1800),  // AI/Technology  
      setTimeout(() => setStage(3), 3200),  // People/Real World
      setTimeout(() => setStage(4), 4500),  // Taraanaya Birth
      setTimeout(() => navigate('/onboarding-new'), 7000)
    ];

    return () => timers.forEach(timer => clearTimeout(timer));
  }, [navigate]);

  return (
    <div 
      className="min-h-screen flex items-center justify-center overflow-hidden relative"
      style={{ 
        background: '#0B0E1A'
      }}
    >
      {/* Radial glow from center - like Dropbox/Lyft */}
      <div 
        className="absolute inset-0"
        style={{
          background: 'radial-gradient(ellipse at center, rgba(34, 211, 238, 0.08) 0%, rgba(139, 92, 246, 0.05) 40%, transparent 70%)'
        }}
      />
      <div className="relative z-10 text-center px-6">
        <AnimatePresence mode="wait">
          {/* Stage 1: Books/Ancient Knowledge - HIGH VISIBILITY */}
          {stage >= 1 && stage < 2 && (
            <motion.div
              key="books"
              className="absolute inset-0 flex items-center justify-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.8 }}
            >
              {/* Central glow */}
              <motion.div
                className="absolute w-96 h-96 rounded-full"
                style={{
                  background: 'radial-gradient(circle, rgba(34, 211, 238, 0.2) 0%, transparent 70%)',
                  filter: 'blur(40px)',
                }}
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.5, 0.8, 0.5],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                }}
              />
              <div className="relative w-96 h-96">
                {[...Array(12)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="absolute"
                    style={{
                      left: `${45 + Math.cos((i * Math.PI) / 6) * 35}%`,
                      top: `${45 + Math.sin((i * Math.PI) / 6) * 35}%`,
                    }}
                    initial={{ opacity: 0, scale: 0, rotate: 0 }}
                    animate={{ 
                      opacity: [0, 0.9, 0.9],
                      scale: [0, 1.2, 1],
                      rotate: [0, 10, -5],
                    }}
                    transition={{
                      duration: 1,
                      delay: i * 0.08,
                      ease: 'easeOut',
                    }}
                  >
                    <svg width="50" height="60" viewBox="0 0 50 60" fill="none">
                      <defs>
                        <linearGradient id={`bookGrad${i}`} x1="0" y1="0" x2="50" y2="60">
                          <stop offset="0%" stopColor="#22D3EE" />
                          <stop offset="100%" stopColor="#A78BFA" />
                        </linearGradient>
                        <filter id={`bookGlow${i}`}>
                          <feGaussianBlur stdDeviation="2" />
                        </filter>
                      </defs>
                      <rect width="50" height="60" rx="3" fill={`url(#bookGrad${i})`} fillOpacity="0.5" filter={`url(#bookGlow${i})`} />
                      <line x1="10" y1="18" x2="40" y2="18" stroke="#22D3EE" strokeWidth="2" strokeOpacity="0.7" />
                      <line x1="10" y1="28" x2="35" y2="28" stroke="#60A5FA" strokeWidth="2" strokeOpacity="0.6" />
                      <line x1="10" y1="38" x2="38" y2="38" stroke="#A78BFA" strokeWidth="2" strokeOpacity="0.6" />
                    </svg>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Stage 2: AI/Technology - Neural network nodes */}
          {stage >= 2 && stage < 3 && (
            <motion.div
              key="ai"
              className="absolute inset-0 flex items-center justify-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ duration: 0.8 }}
            >
              <div className="relative w-96 h-96">
                {/* Neural network nodes */}
                {[...Array(8)].map((_, i) => (
                  <motion.div key={i}>
                    <motion.circle
                      cx="50%"
                      cy="50%"
                      r="4"
                      fill="url(#aiGlow)"
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ 
                        opacity: [0, 1, 0.8],
                        scale: [0, 1.2, 1],
                        x: Math.cos((i * Math.PI) / 4) * 120,
                        y: Math.sin((i * Math.PI) / 4) * 120,
                      }}
                      transition={{
                        duration: 0.8,
                        delay: i * 0.08,
                      }}
                    />
                    {/* Connecting lines */}
                    {i < 7 && (
                      <motion.line
                        x1="50%"
                        y1="50%"
                        x2={`calc(50% + ${Math.cos((i * Math.PI) / 4) * 120}px)`}
                        y2={`calc(50% + ${Math.sin((i * Math.PI) / 4) * 120}px)`}
                        stroke="url(#lineGrad)"
                        strokeWidth="1"
                        strokeOpacity="0.4"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 0.8, delay: 0.3 + i * 0.05 }}
                      />
                    )}
                  </motion.div>
                ))}
                <svg className="absolute inset-0" width="100%" height="100%">
                  <defs>
                    <radialGradient id="aiGlow">
                      <stop offset="0%" stopColor="#22D3EE" />
                      <stop offset="100%" stopColor="#8B5CF6" />
                    </radialGradient>
                    <linearGradient id="lineGrad">
                      <stop offset="0%" stopColor="#22D3EE" stopOpacity="0.6" />
                      <stop offset="100%" stopColor="#8B5CF6" stopOpacity="0.2" />
                    </linearGradient>
                  </defs>
                  {/* Draw neural connections */}
                  {[...Array(8)].map((_, i) => (
                    <React.Fragment key={i}>
                      <motion.line
                        x1="50%"
                        y1="50%"
                        x2={`calc(50% + ${Math.cos((i * Math.PI) / 4) * 120}px)`}
                        y2={`calc(50% + ${Math.sin((i * Math.PI) / 4) * 120}px)`}
                        stroke="url(#lineGrad)"
                        strokeWidth="1.5"
                        initial={{ pathLength: 0, opacity: 0 }}
                        animate={{ pathLength: 1, opacity: 0.5 }}
                        transition={{ duration: 0.6, delay: 0.4 + i * 0.05 }}
                      />
                      <motion.circle
                        cx={`calc(50% + ${Math.cos((i * Math.PI) / 4) * 120}px)`}
                        cy={`calc(50% + ${Math.sin((i * Math.PI) / 4) * 120}px)`}
                        r="6"
                        fill="url(#aiGlow)"
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 0.8, scale: 1 }}
                        transition={{ duration: 0.4, delay: 0.6 + i * 0.05 }}
                      />
                    </React.Fragment>
                  ))}
                  {/* Center AI core */}
                  <motion.circle
                    cx="50%"
                    cy="50%"
                    r="12"
                    fill="url(#aiGlow)"
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: [0, 1.2, 1] }}
                    transition={{ duration: 0.8, delay: 0.2 }}
                  />
                </svg>
              </div>
            </motion.div>
          )}

          {/* Stage 3: People/Real World - Silhouettes and connections */}
          {stage >= 3 && stage < 4 && (
            <motion.div
              key="people"
              className="absolute inset-0 flex items-center justify-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0, scale: 1.5 }}
              transition={{ duration: 0.8 }}
            >
              <div className="relative w-96 h-96">
                {[...Array(6)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="absolute"
                    style={{
                      left: `${40 + Math.cos((i * Math.PI) / 3) * 30}%`,
                      top: `${40 + Math.sin((i * Math.PI) / 3) * 30}%`,
                    }}
                    initial={{ opacity: 0, scale: 0, y: 20 }}
                    animate={{ 
                      opacity: [0, 0.7, 0.7],
                      scale: [0, 1, 1],
                      y: [20, 0, 0],
                    }}
                    transition={{
                      duration: 0.8,
                      delay: i * 0.1,
                    }}
                  >
                    <svg width="50" height="60" viewBox="0 0 50 60">
                      <circle cx="25" cy="15" r="10" fill="url(#personGrad)" fillOpacity="0.5" />
                      <path d="M 25 25 Q 15 35, 10 60 L 40 60 Q 35 35, 25 25 Z" fill="url(#personGrad)" fillOpacity="0.4" />
                      <defs>
                        <linearGradient id="personGrad">
                          <stop offset="0%" stopColor="#22D3EE" />
                          <stop offset="100%" stopColor="#8B5CF6" />
                        </linearGradient>
                      </defs>
                    </svg>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Stage 4: Taraanaya Birth - Logo Reveal */}
          {stage >= 4 && (
            <motion.div
              key="reveal"
              className="relative"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1.2, ease: 'easeOut' }}
            >
              {/* Expanding circle background effect */}
              <motion.div
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full"
                style={{
                  width: 400,
                  height: 400,
                  background: 'radial-gradient(circle, rgba(139, 92, 246, 0.05) 0%, transparent 70%)',
                }}
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1.5, opacity: 1 }}
                transition={{ duration: 1.5, ease: 'easeOut' }}
              />

              {/* MASSIVE radiant glow backdrop - AWARD WINNING VISIBILITY */}
              <motion.div
                className="absolute"
                style={{
                  width: '800px',
                  height: '800px',
                  background: 'radial-gradient(circle, rgba(34, 211, 238, 0.25) 0%, rgba(99, 102, 241, 0.15) 40%, rgba(139, 92, 246, 0.12) 60%, transparent 80%)',
                  filter: 'blur(80px)',
                }}
                initial={{ opacity: 0, scale: 0.3 }}
                animate={{ opacity: 1, scale: 1.3 }}
                transition={{ duration: 2.5, delay: 0.2 }}
              />

              {/* Constellation of 4-pointed stars in arc - HIGH CONTRAST */}
              <motion.div
                className="flex justify-center mb-10 relative z-10"
                initial={{ opacity: 0, scale: 0.3, y: 40 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ 
                  duration: 1.5, 
                  delay: 0.3,
                  ease: [0.25, 0.46, 0.45, 0.94]
                }}
              >
                <svg width="280" height="100" viewBox="0 0 280 100">
                  <defs>
                    <linearGradient id="starGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor="#22D3EE" />
                      <stop offset="30%" stopColor="#60A5FA" />
                      <stop offset="70%" stopColor="#A78BFA" />
                      <stop offset="100%" stopColor="#C084FC" />
                    </linearGradient>
                    <filter id="starGlow" x="-50%" y="-50%" width="200%" height="200%">
                      <feGaussianBlur stdDeviation="6" result="coloredBlur"/>
                      <feMerge>
                        <feMergeNode in="coloredBlur"/>
                        <feMergeNode in="coloredBlur"/>
                        <feMergeNode in="SourceGraphic"/>
                      </feMerge>
                    </filter>
                  </defs>
                  
                  {/* Connecting arc line with glow */}
                  <motion.path
                    d="M 40 75 Q 140 25, 240 75"
                    stroke="url(#starGradient)"
                    strokeWidth="2"
                    fill="none"
                    opacity="0.6"
                    filter="url(#starGlow)"
                    initial={{ pathLength: 0 }}
                    animate={{ pathLength: 1 }}
                    transition={{ duration: 1.5, delay: 0.5 }}
                  />
                  
                  {/* 5 Four-pointed stars along arc - LARGER & BRIGHTER */}
                  {[
                    { x: 40, y: 75, delay: 0.6, size: 14 },
                    { x: 90, y: 48, delay: 0.7, size: 16 },
                    { x: 140, y: 30, delay: 0.8, size: 20 },
                    { x: 190, y: 48, delay: 0.9, size: 16 },
                    { x: 240, y: 75, delay: 1.0, size: 14 },
                  ].map((star, i) => (
                    <motion.g
                      key={i}
                      initial={{ opacity: 0, scale: 0 }}
                      animate={{ 
                        opacity: [0, 1, 1],
                        scale: [0, 1.4, 1],
                      }}
                      transition={{ 
                        duration: 1, 
                        delay: star.delay,
                        ease: 'easeOut'
                      }}
                    >
                      {/* White core for maximum brightness */}
                      <circle
                        cx={star.x}
                        cy={star.y}
                        r={star.size * 0.2}
                        fill="#FFFFFF"
                        opacity="0.9"
                      />
                      {/* 4-pointed geometric star with gradient */}
                      <path
                        d={`
                          M ${star.x} ${star.y - star.size}
                          L ${star.x + star.size * 0.35} ${star.y - star.size * 0.35}
                          L ${star.x + star.size} ${star.y}
                          L ${star.x + star.size * 0.35} ${star.y + star.size * 0.35}
                          L ${star.x} ${star.y + star.size}
                          L ${star.x - star.size * 0.35} ${star.y + star.size * 0.35}
                          L ${star.x - star.size} ${star.y}
                          L ${star.x - star.size * 0.35} ${star.y - star.size * 0.35}
                          Z
                        `}
                        fill="url(#starGradient)"
                        filter="url(#starGlow)"
                        opacity={i === 2 ? "1" : "0.95"}
                      />
                    </motion.g>
                  ))}
                </svg>
              </motion.div>

              {/* "taraanaya" text - EXTREME BRIGHTNESS LIKE DROPBOX/LYFT */}
              <motion.div
                className="relative z-10"
                initial={{ opacity: 0, y: 30, scale: 0.85 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 1.4, delay: 1.2, ease: [0.25, 0.46, 0.45, 0.94] }}
              >
                {/* Multiple glow layers for MAXIMUM visibility */}
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    fontFamily: "'Brush Script MT', 'Lucida Handwriting', cursive",
                    fontSize: '96px',
                    fontWeight: 400,
                    color: '#22D3EE',
                    letterSpacing: '4px',
                    fontStyle: 'italic',
                    filter: 'blur(40px)',
                    opacity: 0.7,
                  }}
                >
                  taraanaya
                </div>
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: '50%',
                    transform: 'translateX(-50%)',
                    fontFamily: "'Brush Script MT', 'Lucida Handwriting', cursive",
                    fontSize: '96px',
                    fontWeight: 400,
                    color: '#FFFFFF',
                    letterSpacing: '4px',
                    fontStyle: 'italic',
                    filter: 'blur(15px)',
                    opacity: 0.4,
                  }}
                >
                  taraanaya
                </div>
                {/* Main gradient text - BRIGHT */}
                <h1
                  style={{
                    fontFamily: "'Brush Script MT', 'Lucida Handwriting', cursive",
                    fontSize: '96px',
                    fontWeight: 400,
                    background: 'linear-gradient(90deg, #22D3EE 0%, #60A5FA 25%, #818CF8 50%, #A78BFA 75%, #C084FC 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    letterSpacing: '4px',
                    textAlign: 'center',
                    fontStyle: 'italic',
                    filter: 'drop-shadow(0 4px 40px rgba(34, 211, 238, 0.8)) drop-shadow(0 4px 80px rgba(139, 92, 246, 0.6)) drop-shadow(0 0 120px rgba(99, 102, 241, 0.4))',
                    position: 'relative',
                  }}
                >
                  taraanaya
                </h1>
              </motion.div>

              {/* Subtle shimmer particles */}
              {[...Array(8)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute rounded-full"
                  style={{
                    width: 2,
                    height: 2,
                    background: i % 2 === 0 ? '#22D3EE' : '#8B5CF6',
                    left: `${35 + i * 8}%`,
                    top: `${45 + Math.sin(i) * 15}%`,
                  }}
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ 
                    opacity: [0, 0.8, 0],
                    scale: [0, 1.5, 0],
                  }}
                  transition={{
                    duration: 2,
                    delay: 1.8 + (i * 0.1),
                    repeat: Infinity,
                    repeatDelay: 2,
                  }}
                />
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default SplashPerfect;
